import { useCallback, useMemo } from 'react';
import { FlagType } from '../../../../../@types/product.enum';

import useCart from '../../../../providers/cart';
import useContext from '../../../../hooks/useContext';
import { useFlags } from '../providers/FlagsProvider';
import { ProductContext } from '../providers/ProductProvider';
import { useTrackGenericEvent } from '../../../../hooks/tracking/useTrackingEvents';

const trackingEvent = { event: 'buybutton_click' };

export default (variantId?: string) => {
  const { sellType, isActive, categorization } = useContext(ProductContext);
  const { addToCart, adding } = useCart();
  const isDisabledReward = useRewardArticle(sellType, variantId);
  const trackBuyButtonClick = useTrackGenericEvent(trackingEvent);
  const { showQuantityDropdown = false } = categorization;

  const buyButtonInactive = !!useFlags(FlagType.buybuttoninactive);

  const click = useCallback(
    (quantity = 1) => {
      if (variantId) {
        trackBuyButtonClick();
        return addToCart(variantId, quantity);
      }
    },
    [variantId, trackBuyButtonClick],
  );

  let message = undefined;

  if (buyButtonInactive) {
    message = `Produkten är just nu inte tillgänglig för köp online. Butikssaldo går att se under lagerstatus.`;
  } else if (isDisabledReward) {
    message = `Denna produkt är del av en kampanj och går endast köpa om kampanjvillkoren är uppfyllda`;
  } else if (!isActive) {
    message = `Denna produkt finns inte i vårt sortiment.`;
  }

  return {
    adding,
    disabled: adding || buyButtonInactive || !variantId || isDisabledReward || !isActive,
    message,
    isActive,
    showQuantityDropdown,
    onAddToCart: click,
  };
};

const useRewardArticle = (sellType?: SellType, variantId?: string) => {
  const { cart } = useCart();

  return useMemo(() => {
    if (sellType !== 'Reward') {
      return false;
    }

    return !cart?.campaignSuggestions?.unactivatedCampaigns?.some(({ rewardItems }) =>
      rewardItems.find((i) => i.variantId === variantId),
    );
  }, [cart, sellType]);
};

export interface IBuyButtonArticle extends Pick<IArticle, 'flags' | 'sellType' | 'id'> {}

export const useArticleBuyButton = (article?: IBuyButtonArticle) => {
  const { flags, sellType, id: variantId } = article || {};

  const isDisabledReward = useRewardArticle(sellType, variantId);
  const { addToCart, adding } = useCart();

  const click = useCallback(
    (quantity?: number) => {
      if (variantId) {
        addToCart(variantId, quantity);
      }
    },
    [variantId],
  );

  const buyButtonInActive = useMemo(
    () => !!flags?.find(({ type }) => type === FlagType.buybuttoninactive),
    [flags],
  );

  return {
    disabled: adding || buyButtonInActive || !variantId || isDisabledReward,
    onAddToCart: click,
  };
};
